import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    Menu, 
    Sparkles,
    RefreshCw, 
    MessageSquare,
    User,
    LogOut,
    Cloud,
    Save,
    Share2,
    Settings,
    Crown,
    FolderOpen
} from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

import './App.css';
import { ProjectProvider, useProjects } from './context/ProjectContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatInput } from './components/ChatInput';
import { BlueprintDisplay } from './components/BlueprintDisplay';
import { ProjectSidebar } from './components/ProjectSidebar';
import { LoadingState } from './components/LoadingState';
import { AuthModal } from './components/AuthModal';
import { FeedbackModal } from './components/FeedbackModal';
import { SavedBlueprintsModal } from './components/SavedBlueprintsModal';
import { UsageIndicator } from './components/UsageIndicator';
import { SharedBlueprintView } from './components/SharedBlueprintView';
import { FeaturedBlueprints } from './components/FeaturedBlueprints';
import AdminDashboard from './components/AdminDashboard';
import { SignupNudge } from './components/SignupNudge';
import { UpgradeModal } from './components/UpgradeModal';
import { CheckoutSuccess } from './components/CheckoutSuccess';
import { ClarificationFlow } from './components/ClarificationFlow';
import { MyProjectsModal } from './components/MyProjectsModal';
import { ResumeCard } from './components/ResumeCard';
import { Button } from './components/ui/button';
import { trackBlueprintGenerated, trackPageVisit, isFromSharedView, clearSharedViewFlag } from './utils/analytics';
import { getSessionId } from './utils/session';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MainApp = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentIdea, setCurrentIdea] = useState('');
    const [error, setError] = useState(null);
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authModalMode, setAuthModalMode] = useState('login');
    const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
    const [savedBlueprintsModalOpen, setSavedBlueprintsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const [usageKey, setUsageKey] = useState(0); // For refreshing usage indicator
    const [sharedBlueprintId, setSharedBlueprintId] = useState(null);
    const [showAdminDashboard, setShowAdminDashboard] = useState(false);
    const [showSignupNudge, setShowSignupNudge] = useState(false);
    const [pendingBlueprintToSave, setPendingBlueprintToSave] = useState(null);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [upgradeContext, setUpgradeContext] = useState('nav');
    const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);
    const [currentUsage, setCurrentUsage] = useState(null);
    
    // Project persistence state
    const [myProjectsModalOpen, setMyProjectsModalOpen] = useState(false);
    const [currentProjectId, setCurrentProjectId] = useState(null);
    const [currentProjectData, setCurrentProjectData] = useState(null);
    const [hitPaywall, setHitPaywall] = useState(() => {
        // Check if user hit paywall in last session
        return localStorage.getItem('vcc_hit_paywall') === 'true';
    });
    
    // Clarification flow state
    const [showClarification, setShowClarification] = useState(false);
    const [pendingIdea, setPendingIdea] = useState('');
    const [clarifyEnabled, setClarifyEnabled] = useState(() => {
        // Default ON for new users, respect saved preference
        const saved = localStorage.getItem('vcc_clarify_enabled');
        return saved === null ? true : saved === 'true';
    });
    
    // Track clarification usage for current blueprint
    const [lastUsedClarification, setLastUsedClarification] = useState(false);
    const [lastClarificationSkipped, setLastClarificationSkipped] = useState(false);
    const [lastPromptId, setLastPromptId] = useState(null);
    
    const { currentProject, saveProject, updateProject, clearCurrentProject } = useProjects();
    const { user, isAuthenticated, isAdmin, logout, getAuthHeaders, token } = useAuth();

    // Handle URL routing for shared blueprints, admin, and checkout
    useEffect(() => {
        const handleRouting = () => {
            const path = window.location.pathname;
            const params = new URLSearchParams(window.location.search);
            
            if (path === '/checkout/success' || params.get('session_id')) {
                setShowCheckoutSuccess(true);
                setShowAdminDashboard(false);
                setSharedBlueprintId(null);
            } else if (path === '/admin') {
                setShowAdminDashboard(true);
                setSharedBlueprintId(null);
                setShowCheckoutSuccess(false);
            } else if (path.startsWith('/b/')) {
                const shortId = path.substring(3);
                if (shortId) {
                    setSharedBlueprintId(shortId);
                }
                setShowAdminDashboard(false);
                setShowCheckoutSuccess(false);
            } else {
                setSharedBlueprintId(null);
                setShowAdminDashboard(false);
                setShowCheckoutSuccess(false);
            }
        };

        handleRouting();
        window.addEventListener('popstate', handleRouting);
        return () => window.removeEventListener('popstate', handleRouting);
    }, []);

    // Track page visit on mount
    useEffect(() => {
        trackPageVisit();
    }, []);

    const navigateToHome = () => {
        window.history.pushState({}, '', '/');
        setSharedBlueprintId(null);
        setShowAdminDashboard(false);
        setShowCheckoutSuccess(false);
        refreshUsage();
        // Clear paywall flag after successful checkout
        setHitPaywall(false);
        localStorage.removeItem('vcc_hit_paywall');
    };

    const navigateToSharedBlueprint = (shortId) => {
        window.history.pushState({}, '', `/b/${shortId}`);
        setSharedBlueprintId(shortId);
        setShowAdminDashboard(false);
    };

    const navigateToAdmin = () => {
        window.history.pushState({}, '', '/admin');
        setShowAdminDashboard(true);
        setSharedBlueprintId(null);
    };

    const refreshUsage = useCallback(() => {
        setUsageKey(prev => prev + 1);
    }, []);

    // Upgrade modal handlers
    const openUpgradeModal = (context = 'nav') => {
        if (!isAuthenticated) {
            setAuthModalMode('login');
            setAuthModalOpen(true);
            toast.info('Please sign in first to upgrade to Pro');
            return;
        }
        setUpgradeContext(context);
        setUpgradeModalOpen(true);
        
        // Track that user hit a paywall (for resume card upsell)
        setHitPaywall(true);
        localStorage.setItem('vcc_hit_paywall', 'true');
    };

    // Toggle clarification preference
    const toggleClarifyEnabled = () => {
        const newValue = !clarifyEnabled;
        setClarifyEnabled(newValue);
        localStorage.setItem('vcc_clarify_enabled', String(newValue));
    };

    // Handle initial idea submission (may trigger clarification)
    const handleIdeaSubmit = (idea) => {
        if (clarifyEnabled) {
            // Show clarification flow
            setPendingIdea(idea);
            setShowClarification(true);
            setCurrentIdea(idea);
        } else {
            // Skip clarification, generate directly
            handleGenerateBlueprint(idea);
        }
    };

    // Handle clarification completion
    const handleClarificationComplete = (finalPrompt, wasRefined) => {
        setShowClarification(false);
        setPendingIdea('');
        setLastUsedClarification(wasRefined);
        setLastClarificationSkipped(false);
        setLastPromptId(`prompt-${Date.now()}`);
        handleGenerateBlueprint(finalPrompt);
    };

    // Handle skip clarification
    const handleSkipClarification = (originalIdea) => {
        setShowClarification(false);
        setPendingIdea('');
        setLastUsedClarification(false);
        setLastClarificationSkipped(true);
        setLastPromptId(`prompt-${Date.now()}`);
        handleGenerateBlueprint(originalIdea);
    };

    const handleGenerateBlueprint = async (idea) => {
        setIsLoading(true);
        setCurrentIdea(idea);
        setError(null);
        clearCurrentProject();
        setShowClarification(false); // Ensure clarification is hidden during generation

        try {
            const sessionId = getSessionId();
            const headers = isAuthenticated ? getAuthHeaders() : {};
            
            const response = await axios.post(
                `${API}/generate-blueprint`, 
                { idea, session_id: sessionId }, 
                { timeout: 60000, headers }
            );
            
            if (response.data.success && response.data.blueprint) {
                saveProject(idea, response.data.blueprint);
                toast.success('App architecture generated successfully!');
                
                // Auto-create project for authenticated users
                if (isAuthenticated) {
                    try {
                        // Create project and save blueprint
                        const projectResponse = await axios.post(`${API}/projects`, {
                            original_prompt: idea,
                            refined_prompt: lastUsedClarification ? idea : null,
                            blueprint: response.data.blueprint,
                            name: response.data.blueprint.app_name
                        }, { headers: getAuthHeaders() });
                        
                        setCurrentProjectId(projectResponse.data.id);
                        setCurrentProjectData(projectResponse.data);
                    } catch (projectError) {
                        console.warn('Project auto-save failed:', projectError);
                    }
                }
                
                // Track with fromSharedView flag to correctly attribute conversions
                const fromShared = isFromSharedView();
                trackBlueprintGenerated(response.data.blueprint.app_name, idea.length, fromShared);
                
                // Clear the shared view flag after tracking (user has now taken an action)
                if (fromShared) {
                    clearSharedViewFlag();
                }
                
                // Refresh usage after successful generation
                refreshUsage();
                
                // Show signup nudge for anonymous users after blueprint generation
                if (!isAuthenticated) {
                    setPendingBlueprintToSave({
                        idea,
                        blueprint: response.data.blueprint
                    });
                    // Delay showing the nudge slightly so user sees the blueprint first
                    setTimeout(() => {
                        setShowSignupNudge(true);
                    }, 2000);
                }
            } else {
                const errorMsg = response.data.error || 'Failed to generate architecture. Please try again.';
                setError(errorMsg);
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('Error generating blueprint:', error);
            let errorMessage = 'Something went wrong generating your architecture. Please try again.';
            
            if (error.code === 'ECONNABORTED') {
                errorMessage = 'Request timed out. Please try a simpler app idea or try again.';
            } else if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            }
            
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveToAccount = async () => {
        if (!isAuthenticated) {
            setAuthModalMode('login');
            setAuthModalOpen(true);
            return;
        }

        if (!currentProject) return;

        setIsSaving(true);
        try {
            await axios.post(`${API}/blueprints/save`, {
                idea: currentProject.idea,
                blueprint: currentProject.blueprint,
                name: currentProject.blueprint.app_name
            }, { headers: getAuthHeaders() });
            
            toast.success('Blueprint saved to your account!');
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Failed to save blueprint. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSelectSavedBlueprint = (idea, blueprint) => {
        saveProject(idea, blueprint);
        setCurrentIdea(idea);
    };

    // Project persistence handlers
    const handleSelectProject = (project) => {
        // Load project data into the app
        setCurrentProjectId(project.id);
        setCurrentProjectData(project);
        
        if (project.blueprint) {
            saveProject(project.original_prompt, project.blueprint);
            setCurrentIdea(project.original_prompt);
        } else {
            // No blueprint yet - set up for new generation
            setCurrentIdea(project.original_prompt);
            clearCurrentProject();
        }
        
        toast.success(`Loaded: ${project.name}`);
    };

    const handleCreateProject = async (originalPrompt, refinedPrompt = null) => {
        if (!isAuthenticated) return null;
        
        try {
            const response = await axios.post(`${API}/projects`, {
                original_prompt: originalPrompt,
                refined_prompt: refinedPrompt
            }, { headers: getAuthHeaders() });
            
            setCurrentProjectId(response.data.id);
            setCurrentProjectData(response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to create project:', error);
            return null;
        }
    };

    const handleSaveBlueprintToProject = async (blueprint) => {
        if (!currentProjectId || !isAuthenticated) return;
        
        try {
            const response = await axios.post(
                `${API}/projects/${currentProjectId}/blueprint`,
                blueprint,
                { headers: getAuthHeaders() }
            );
            setCurrentProjectData(response.data);
        } catch (error) {
            console.error('Failed to save blueprint to project:', error);
        }
    };

    const handleContinueBuildGenerated = (option, content) => {
        // Update local project data when continue-build is auto-saved
        if (currentProjectData) {
            setCurrentProjectData(prev => ({
                ...prev,
                continue_builds: [
                    ...(prev.continue_builds || []),
                    { option, content, created_at: new Date().toISOString() }
                ]
            }));
        }
    };

    const handleShareBlueprint = async () => {
        if (!isAuthenticated) {
            setAuthModalMode('login');
            setAuthModalOpen(true);
            toast.info('Please sign in to share blueprints');
            return;
        }

        if (!currentProject) return;

        setIsSharing(true);
        try {
            const response = await axios.post(`${API}/share`, {
                idea: currentProject.idea,
                blueprint: currentProject.blueprint
            }, { headers: getAuthHeaders() });

            const shareUrl = `${window.location.origin}${response.data.share_url}`;
            await navigator.clipboard.writeText(shareUrl);
            toast.success('Share link copied to clipboard!');
        } catch (error) {
            console.error('Share error:', error);
            toast.error('Failed to create share link. Please try again.');
        } finally {
            setIsSharing(false);
        }
    };

    const handleChecklistToggle = (item) => {
        if (!currentProject) return;

        const updatedChecklist = currentProject.blueprint.checklist.map(checkItem =>
            checkItem.task === item.task
                ? { ...checkItem, completed: !checkItem.completed }
                : checkItem
        );

        updateProject(currentProject.id, {
            blueprint: {
                ...currentProject.blueprint,
                checklist: updatedChecklist
            }
        });
    };

    const handleNewProject = () => {
        // Clear project persistence state
        setCurrentProjectId(null);
        setCurrentProjectData(null);
        // Clear local project state
        clearCurrentProject();
        setCurrentIdea('');
        setError(null);
        setSidebarOpen(false);
        setShowSignupNudge(false);
        setShowClarification(false);
    };

    const handleOpenAuth = (mode) => {
        setAuthModalMode(mode);
        setAuthModalOpen(true);
    };

    const handleSignupFromNudge = () => {
        setShowSignupNudge(false);
        setAuthModalMode('signup');
        setAuthModalOpen(true);
    };

    const handleDismissNudge = () => {
        setShowSignupNudge(false);
        setPendingBlueprintToSave(null);
    };

    // Auto-save blueprint after successful signup (if there was a pending blueprint)
    useEffect(() => {
        const autoSaveAfterSignup = async () => {
            if (isAuthenticated && pendingBlueprintToSave && token) {
                try {
                    await axios.post(`${API}/blueprints/save`, pendingBlueprintToSave, {
                        headers: getAuthHeaders()
                    });
                    toast.success('Blueprint saved to your account!');
                    setPendingBlueprintToSave(null);
                } catch (error) {
                    console.error('Auto-save failed:', error);
                    // Don't show error - not critical
                }
            }
        };
        autoSaveAfterSignup();
    }, [isAuthenticated, pendingBlueprintToSave, token, getAuthHeaders]);

    const showBlueprint = currentProject?.blueprint && !isLoading && !sharedBlueprintId && !showAdminDashboard && !showCheckoutSuccess;
    const showWelcome = !currentProject && !isLoading && !error && !sharedBlueprintId && !showAdminDashboard && !showCheckoutSuccess;
    const showSharedView = !!sharedBlueprintId && !showAdminDashboard && !showCheckoutSuccess;

    // Show checkout success page
    if (showCheckoutSuccess) {
        return (
            <div className="app-container min-h-screen">
                <CheckoutSuccess onNavigateHome={navigateToHome} />
                <Toaster richColors position="top-center" />
            </div>
        );
    }

    return (
        <div className="app-container min-h-screen flex">
            {/* Sidebar */}
            <ProjectSidebar 
                isOpen={sidebarOpen} 
                onClose={() => setSidebarOpen(false)}
                onNewProject={handleNewProject}
            />

            {/* Main content */}
            <main className="flex-1 flex flex-col min-h-screen">
                {/* Header */}
                <header className="sticky top-0 z-30 glass px-4 md:px-6 py-4">
                    <div className="flex items-center justify-between max-w-5xl mx-auto">
                        <div className="flex items-center gap-3">
                            <Button
                                data-testid="toggle-sidebar-btn"
                                variant="ghost"
                                size="icon"
                                onClick={() => setSidebarOpen(true)}
                                className="hover:bg-white/10"
                            >
                                <Menu className="w-5 h-5" />
                            </Button>
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 sm:p-2 rounded-lg bg-primary/20">
                                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                                </div>
                                <div>
                                    <span className="font-heading font-semibold text-sm sm:text-lg block leading-tight">
                                        Vibe Coding Coach
                                    </span>
                                    <span className="hidden sm:block text-xs text-gray-500">
                                        Plan your AI app fast
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            {/* Usage Indicator with Upgrade Button - Desktop */}
                            <div className="hidden sm:block">
                                <UsageIndicator 
                                    key={usageKey} 
                                    onUsageUpdate={setCurrentUsage}
                                    onUpgradeClick={() => openUpgradeModal('nav')} 
                                />
                            </div>

                            {/* Mobile Upgrade Button - Only shows on small screens for non-pro users */}
                            {currentUsage && !currentUsage.is_pro && (
                                <button
                                    onClick={() => openUpgradeModal('nav')}
                                    className="sm:hidden flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white"
                                    data-testid="upgrade-btn-mobile"
                                >
                                    <Crown className="w-3 h-3" />
                                    <span>Pro</span>
                                </button>
                            )}

                            {/* Feedback Button */}
                            <Button
                                data-testid="feedback-btn"
                                variant="ghost"
                                size="sm"
                                onClick={() => setFeedbackModalOpen(true)}
                                className="hidden md:flex hover:bg-white/10 text-gray-400"
                            >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Feedback
                            </Button>

                            {/* Saved Blueprints Button */}
                            {isAuthenticated && (
                                <Button
                                    data-testid="saved-blueprints-btn"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSavedBlueprintsModalOpen(true)}
                                    className="hidden md:flex hover:bg-white/10 text-gray-400"
                                >
                                    <Cloud className="w-4 h-4 mr-2" />
                                    Saved
                                </Button>
                            )}

                            {/* My Projects Button */}
                            {isAuthenticated && (
                                <Button
                                    data-testid="my-projects-btn"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setMyProjectsModalOpen(true)}
                                    className="hidden md:flex hover:bg-white/10 text-gray-400"
                                >
                                    <FolderOpen className="w-4 h-4 mr-2" />
                                    Projects
                                </Button>
                            )}

                            {/* Admin Dashboard Button */}
                            {isAdmin && (
                                <Button
                                    data-testid="admin-dashboard-btn"
                                    variant="ghost"
                                    size="sm"
                                    onClick={navigateToAdmin}
                                    className="hidden md:flex hover:bg-white/10 text-yellow-500"
                                >
                                    <Settings className="w-4 h-4 mr-2" />
                                    Admin
                                </Button>
                            )}

                            {/* New Project Button */}
                            <Button
                                data-testid="new-blueprint-btn"
                                onClick={handleNewProject}
                                variant="outline"
                                size="sm"
                                className="bg-white/5 border-white/10 hover:bg-white/10"
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                <span className="hidden sm:inline">New Project</span>
                            </Button>

                            {/* Auth / User Menu */}
                            {isAuthenticated ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            data-testid="user-menu-btn"
                                            variant="outline"
                                            size="sm"
                                            className="bg-primary/20 border-primary/30 hover:bg-primary/30"
                                        >
                                            <User className="w-4 h-4 mr-2" />
                                            <span className="hidden sm:inline max-w-[100px] truncate">
                                                {user?.name || user?.email?.split('@')[0]}
                                            </span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                        <DropdownMenuItem 
                                            onClick={() => setSavedBlueprintsModalOpen(true)}
                                            className="cursor-pointer"
                                        >
                                            <Cloud className="w-4 h-4 mr-2" />
                                            Saved Blueprints
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                            onClick={() => setMyProjectsModalOpen(true)}
                                            className="cursor-pointer"
                                        >
                                            <FolderOpen className="w-4 h-4 mr-2" />
                                            My Projects
                                        </DropdownMenuItem>
                                        {isAdmin && (
                                            <DropdownMenuItem 
                                                onClick={navigateToAdmin}
                                                className="cursor-pointer text-yellow-500"
                                            >
                                                <Settings className="w-4 h-4 mr-2" />
                                                Admin Dashboard
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem 
                                            onClick={() => setFeedbackModalOpen(true)}
                                            className="cursor-pointer"
                                        >
                                            <MessageSquare className="w-4 h-4 mr-2" />
                                            Send Feedback
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                            onClick={logout}
                                            className="cursor-pointer text-destructive"
                                        >
                                            <LogOut className="w-4 h-4 mr-2" />
                                            Sign Out
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <Button
                                    data-testid="sign-in-btn"
                                    onClick={() => handleOpenAuth('login')}
                                    variant="outline"
                                    size="sm"
                                    className="bg-primary/20 border-primary/30 hover:bg-primary/30"
                                >
                                    <User className="w-4 h-4 mr-2" />
                                    <span className="hidden sm:inline">Sign In</span>
                                </Button>
                            )}
                        </div>
                    </div>
                </header>

                {/* Content area */}
                <div className="flex-1 px-2 sm:px-4 md:px-6 py-8 overflow-x-hidden">
                    <div className="max-w-5xl mx-auto space-y-8 overflow-x-hidden">
                        {/* Resume Card - Continue where you left off */}
                        {!showAdminDashboard && !showSharedView && !showCheckoutSuccess && !currentProject?.blueprint && !showClarification && !isLoading && (
                            <ResumeCard
                                onContinue={handleSelectProject}
                                onViewProjects={() => setMyProjectsModalOpen(true)}
                                isPro={currentUsage?.is_pro || false}
                                onUpgrade={() => openUpgradeModal('resume')}
                                hitPaywall={hitPaywall}
                            />
                        )}

                        {/* Admin Dashboard */}
                        {showAdminDashboard && (
                            <AdminDashboard onBack={navigateToHome} />
                        )}

                        {/* Welcome section */}
                        {/* Hero Section - Simple and focused */}
                        {showWelcome && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="text-center pt-4 pb-10"
                            >
                                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5">
                                    Build an AI App in 30 Seconds
                                </h1>
                                
                                <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto mb-2">
                                    Turn your idea into a complete AI app blueprint using one simple prompt.
                                </p>
                                <p className="text-zinc-500 text-sm">
                                    No coding. No wasted AI credits.
                                </p>
                            </motion.div>
                        )}

                        {/* Chat input - hide when viewing shared blueprint or admin */}
                        {!showSharedView && !showAdminDashboard && !showClarification && (
                            <div className="space-y-3">
                                <ChatInput 
                                    onSubmit={handleIdeaSubmit}
                                    isLoading={isLoading}
                                    disabled={false}
                                    showExamples={showWelcome}
                                />
                                {/* Smart Clarify toggle */}
                                <div className="flex items-center justify-center gap-2 text-xs text-zinc-500">
                                    <button
                                        onClick={toggleClarifyEnabled}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${
                                            clarifyEnabled 
                                                ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30' 
                                                : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                                        }`}
                                        data-testid="clarify-toggle"
                                    >
                                        <Sparkles className="w-3 h-3" />
                                        Smart Clarify {clarifyEnabled ? 'ON' : 'OFF'}
                                    </button>
                                    <span className="hidden sm:inline text-zinc-600">
                                        {clarifyEnabled ? 'AI will ask questions to improve your blueprint' : 'Generate blueprint directly'}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Clarification Flow */}
                        {showClarification && !showAdminDashboard && (
                            <ClarificationFlow
                                originalIdea={pendingIdea}
                                onComplete={handleClarificationComplete}
                                onSkip={handleSkipClarification}
                                isEnabled={clarifyEnabled}
                            />
                        )}

                        {/* Featured Blueprints - show only on welcome screen */}
                        {showWelcome && (
                            <FeaturedBlueprints 
                                onViewBlueprint={navigateToSharedBlueprint}
                                onRemixIdea={(idea) => {
                                    // Pre-fill the ChatInput with the idea
                                    const inputElement = document.querySelector('[data-testid="idea-input"]');
                                    if (inputElement) {
                                        // Trigger a synthetic input event
                                        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
                                        nativeInputValueSetter.call(inputElement, idea);
                                        inputElement.dispatchEvent(new Event('input', { bubbles: true }));
                                    }
                                }}
                            />
                        )}

                        {/* Error state */}
                        {error && !isLoading && !currentProject && !showAdminDashboard && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="glass-card p-6 border border-destructive/30 text-center"
                                data-testid="error-state"
                            >
                                <p className="text-destructive mb-4">{error}</p>
                                <Button
                                    onClick={() => setError(null)}
                                    variant="outline"
                                    className="bg-white/5 border-white/10"
                                >
                                    Try Again
                                </Button>
                            </motion.div>
                        )}

                        {/* Loading state */}
                        {isLoading && !showAdminDashboard && <LoadingState />}

                        {/* Action buttons after generation */}
                        {showBlueprint && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex flex-wrap justify-center gap-3"
                            >
                                <Button
                                    data-testid="start-new-blueprint-btn"
                                    onClick={handleNewProject}
                                    variant="outline"
                                    size="lg"
                                    className="bg-white/5 border-white/10 hover:bg-primary/20 hover:border-primary/30"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Start New Project
                                </Button>
                                <Button
                                    data-testid="save-to-account-btn"
                                    onClick={handleSaveToAccount}
                                    variant="outline"
                                    size="lg"
                                    disabled={isSaving}
                                    className="bg-primary/20 border-primary/30 hover:bg-primary/30"
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    {isSaving ? 'Saving...' : isAuthenticated ? 'Save to Account' : 'Sign In to Save'}
                                </Button>
                            </motion.div>
                        )}

                        {/* Blueprint display */}
                        {showBlueprint && (
                            <>
                                <BlueprintDisplay 
                                    blueprint={currentProject.blueprint}
                                    idea={currentProject.idea}
                                    onChecklistToggle={handleChecklistToggle}
                                    onShare={handleShareBlueprint}
                                    isSharing={isSharing}
                                    isAuthenticated={isAuthenticated}
                                    isPro={currentUsage?.is_pro || false}
                                    usedClarification={lastUsedClarification}
                                    clarificationSkipped={lastClarificationSkipped}
                                    promptId={lastPromptId}
                                    onUpgrade={(context) => openUpgradeModal(context || 'continue')}
                                    projectId={currentProjectId}
                                    onContinueBuildGenerated={handleContinueBuildGenerated}
                                />
                                
                                {/* Post-generation upgrade prompt for free users */}
                                {isAuthenticated && currentUsage && !currentUsage.is_pro && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.5 }}
                                        className="mt-6 p-6 rounded-xl bg-gradient-to-r from-violet-900/30 via-surface to-fuchsia-900/20 border border-violet-500/20"
                                    >
                                        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                            <div>
                                                <h3 className="font-heading font-semibold text-lg text-white mb-1">
                                                    Want to keep building this idea?
                                                </h3>
                                                <p className="text-sm text-gray-400">
                                                    Upgrade to Pro for 30 blueprints/day and unlock continue & refine features.
                                                </p>
                                            </div>
                                            <Button
                                                onClick={() => openUpgradeModal('generation')}
                                                className="whitespace-nowrap bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500"
                                                data-testid="upgrade-after-gen-btn"
                                            >
                                                Upgrade to Pro - $11/mo
                                            </Button>
                                        </div>
                                    </motion.div>
                                )}
                            </>
                        )}

                        {/* Shared Blueprint View */}
                        {showSharedView && (
                            <SharedBlueprintView
                                shortId={sharedBlueprintId}
                                onBack={navigateToHome}
                            />
                        )}
                    </div>
                </div>

                {/* Footer */}
                <footer className="px-4 py-6 border-t border-white/5">
                    <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-sm text-gray-400 text-center md:text-left">
                            Vibe Coding Coach helps builders plan AI-powered apps faster by turning ideas into structured development architecture.
                        </p>
                        <Button
                            data-testid="footer-feedback-btn"
                            variant="ghost"
                            size="sm"
                            onClick={() => setFeedbackModalOpen(true)}
                            className="text-gray-500 hover:text-white hover:bg-white/10"
                        >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Send Feedback
                        </Button>
                    </div>
                </footer>
            </main>

            {/* Modals */}
            <AuthModal 
                isOpen={authModalOpen} 
                onClose={() => setAuthModalOpen(false)}
                initialMode={authModalMode}
            />
            <FeedbackModal 
                isOpen={feedbackModalOpen} 
                onClose={() => setFeedbackModalOpen(false)}
            />
            <SavedBlueprintsModal
                isOpen={savedBlueprintsModalOpen}
                onClose={() => setSavedBlueprintsModalOpen(false)}
                onSelectBlueprint={handleSelectSavedBlueprint}
            />

            {/* My Projects Modal */}
            <MyProjectsModal
                isOpen={myProjectsModalOpen}
                onClose={() => setMyProjectsModalOpen(false)}
                onSelectProject={handleSelectProject}
                onNewProject={handleNewProject}
            />

            {/* Signup Nudge for anonymous users */}
            <SignupNudge
                isVisible={showSignupNudge && !isAuthenticated}
                onSignUp={handleSignupFromNudge}
                onDismiss={handleDismissNudge}
                blueprintName={currentProject?.blueprint?.app_name}
            />

            {/* Upgrade Modal */}
            <UpgradeModal
                isOpen={upgradeModalOpen}
                onClose={() => setUpgradeModalOpen(false)}
                triggerContext={upgradeContext}
            />

            {/* Toast notifications */}
            <Toaster 
                position="top-center"
                toastOptions={{
                    style: {
                        background: '#0A0A0A',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: '#fff',
                    },
                }}
            />
        </div>
    );
};

function App() {
    return (
        <AuthProvider>
            <ProjectProvider>
                <MainApp />
            </ProjectProvider>
        </AuthProvider>
    );
}

export default App;
